import React, { useState, useEffect } from "react";
import { Users } from "lucide-react";

export const PodComposition = () => {
  const [animatedCount, setAnimatedCount] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimatedCount((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  const podSizes = [
    {
      size: "2-Person",
      use: "Specialized projects, rapid prototypes",
      icon: "🎯",
    },
    {
      size: "3-Person",
      use: "Focused development, content creation",
      icon: "⚡",
    },
    {
      size: "5-Person",
      use: "Full-stack projects, comprehensive coverage",
      icon: "🌟",
    },
    {
      size: "6+ Person",
      use: "Enterprise solutions, complex initiatives",
      icon: "🚀",
    },
  ];

  const teamRoles = [
    {
      role: "Project Manager",
      count: 1,
      color: "bg-green-600",
      icon: "👨‍💼",
      description:
        "Timeline management, risk mitigation, stakeholder communication",
    },
    {
      role: "Developers",
      count: "2-3",
      color: "bg-green-500",
      icon: "👩‍💻",
      description:
        "Technical engine with built-in redundancy for knowledge sharing",
    },
    {
      role: "QA Lead",
      count: 1,
      color: "bg-green-700",
      icon: "🔍",
      description:
        "Embedded quality champion ensuring excellence at every stage",
    },
    {
      role: "Flex Specialists",
      count: "0-2",
      color: "bg-orange-500",
      icon: "🎨",
      description:
        "Adaptable experts (Design, Data, DevOps, AI) scaled to project needs",
    },
  ];

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-brand-secondary border border-brand-primary/20 text-brand-tertiary text-sm font-semibold mb-6">
            <Users className="w-4 h-4 mr-2" />
            The Kolaborate Pod Strategy
          </div>
          <h2 className="text-4xl font-bold text-brand-primary mb-6">
            Right-Sized Teams for Every Challenge
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Our recommended 5-person structure is just the starting point. Pods
            scale from 2 to 6+ members, precisely tailored to your project
            complexity and business objectives.
          </p>
        </div>

        {/* Recommended 5-Person Team Structure */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-brand-grey text-center mb-4">
            Recommended: 5-Person Cross-Functional Team
          </h3>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Optimal balance of expertise, collaboration, and cost-effectiveness
          </p>

          <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {teamRoles.map((role, index) => (
                <div key={index} className="text-center">
                  <div
                    className={`w-20 h-20 ${role.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}
                  >
                    <span className="text-white font-bold text-2xl">
                      {role.count}
                    </span>
                  </div>
                  <div className="text-4xl mb-3">{role.icon}</div>
                  <h4 className="text-lg font-bold text-brand-grey mb-2">
                    {role.role}
                  </h4>
                  <p className="text-sm text-gray-500">{role.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Flexible Pod Scaling Options */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4 text-brand-primary">
              Flexible by Design, Powerful by Nature
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every client is unique. Every challenge is different. That's why
              our Pod composition adapts to your exact needs— not the other way
              around.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {podSizes.map((pod, index) => (
              <div
                key={index}
                className={`bg-white border border-gray-200 rounded-3xl shadow-lg p-6 transition-all duration-500 transform hover:-translate-y-1 hover:shadow-xl ${
                  index === animatedCount
                    ? "ring-4 ring-brand-primary/20 scale-105"
                    : ""
                }`}
              >
                <div className="text-center">
                  <div className="text-4xl mb-4">{pod.icon}</div>
                  <h4 className="text-2xl font-bold text-brand-grey mb-2">
                    {pod.size}
                  </h4>
                  <p className="text-gray-600 text-sm">{pod.use}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm max-w-5xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h5 className="font-bold text-green-700 mb-1">Lean Pods (2-3)</h5>
              <p className="text-green-600">
                Specialized tasks, rapid prototypes, focused expertise
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h5 className="font-bold text-green-700 mb-1">
                Standard Pods (4-5)
              </h5>
              <p className="text-green-600">
                Full-stack projects, comprehensive coverage, proven structure
              </p>
            </div>
            <div className="bg-green-100 border border-green-300 rounded-xl p-4">
              <h5 className="font-bold text-green-800 mb-1">
                Enterprise Pods (6+)
              </h5>
              <p className="text-green-700">
                Complex initiatives, multiple workstreams, enterprise scale
              </p>
            </div>
          </div>
        </div>

        {/* Dynamic Pricing Model */}
        <div className="bg-brand-secondary rounded-3xl p-8 text-center max-w-4xl mx-auto">
          <h4 className="text-2xl font-bold mb-4 text-brand-grey">
            Dynamic Pricing Model
          </h4>
          <p className="text-lg text-gray-600 mb-6">
            Forget fixed pricing models. Our Pod investment scales with your
            ambitions. Whether you need a lean 2-person specialty team or a
            robust 6+ person enterprise solution, you pay for the precise
            expertise and capacity that drives your outcomes.
          </p>
          <p className="text-gray-600">
            Investment scales with your ambitions—from lean specialty teams to
            robust enterprise solutions.
          </p>
        </div>
      </div>
    </section>
  );
};
